import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { useSmartBack } from '../hooks/useSmartBack';
import { AppHeader } from '../components/AppHeader';
import './FriendsPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const LOCALE_TAG: Record<string, string> = {
  tr: 'tr-TR', en: 'en-US', nl: 'nl-NL', fr: 'fr-FR', de: 'de-DE',
  it: 'it-IT', pl: 'pl-PL', ru: 'ru-RU', es: 'es-ES',
};

function formatTime(seconds: number): string {
  const s = Math.round(seconds);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

interface Friend {
  friendship_id: number;
  user_id: string;
  nickname: string;
  is_online?: boolean;
}

interface FriendRequest {
  friendship_id: number;
  user_id: string;
  nickname: string;
}

interface SearchUser {
  user_id: string;
  nickname: string;
  friendship_status: string | null;
}

interface FriendMatch {
  id: string;
  difficulty: number;
  room_name: string | null;
  created_at: string | null;
  my_score: number;
  my_place: number;
  my_elapsed: number;
  my_mistakes: number;
  friend_score: number;
  friend_place: number;
  friend_elapsed: number;
  friend_mistakes: number;
}

interface FriendMatchHistory {
  friend: { user_id: string; nickname: string };
  wins: number;
  losses: number;
  draws: number;
  matches: FriendMatch[];
}

export function FriendsPage() {
  const navigate = useNavigate();
  const { user, token, loading } = useAuth();
  const { t, locale } = useI18n();
  const localeTag = LOCALE_TAG[locale] ?? 'en-US';
  const smartBack = useSmartBack('/');

  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [sent, setSent] = useState<FriendRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState<'friends' | 'requests' | 'sent' | 'search'>('friends');
  const [message, setMessage] = useState('');
  const [matchHistory, setMatchHistory] = useState<FriendMatchHistory | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set());

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth'); return; }
    loadFriends();
    loadRequests();
    loadSent();
  }, [user, loading]);

  const loadFriends = async () => {
    const res = await fetch(`${API}/friends`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setFriends(data.friends);
    }
    // Online status
    try {
      const onlineRes = await fetch(`${API}/friends/online`, { headers: authHeaders() });
      if (onlineRes.ok) {
        const onlineData = await onlineRes.json();
        setOnlineIds(new Set(onlineData.online_user_ids));
      }
    } catch {}
  };

  const loadRequests = async () => {
    const res = await fetch(`${API}/friends/requests`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setRequests(data.requests);
    }
  };

  const loadSent = async () => {
    const res = await fetch(`${API}/friends/sent`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setSent(data.sent);
    }
  };

  const openMatchHistory = async (friendUserId: string) => {
    setLoadingHistory(true);
    setMatchHistory(null);
    try {
      const res = await fetch(`${API}/friends/${friendUserId}/matches`, { headers: authHeaders() });
      if (res.ok) {
        setMatchHistory(await res.json());
      } else {
        setMessage(t.friends.historyLoadFailed);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch {
      setMessage(t.friends.networkError);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoadingHistory(false);
    }
  };

  const cancelRequest = async (friendshipId: number) => {
    const res = await fetch(`${API}/friends/${friendshipId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setSent(prev => prev.filter(s => s.friendship_id !== friendshipId));
    }
  };

  const search = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    const res = await fetch(`${API}/friends/search?q=${encodeURIComponent(searchQuery)}`, {
      headers: authHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      setSearchResults(data.users);
    }
    setSearching(false);
  };

  const sendRequest = async (nickname: string) => {
    const res = await fetch(`${API}/friends/request`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ nickname }),
    });
    const data = await res.json();
    if (res.ok) {
      setMessage(data.message);
      setSearchResults(prev =>
        prev.map(u => u.nickname === nickname ? { ...u, friendship_status: 'pending' } : u)
      );
    } else {
      setMessage(data.detail || t.friends.genericError);
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const acceptRequest = async (friendshipId: number) => {
    const res = await fetch(`${API}/friends/${friendshipId}/accept`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (res.ok) {
      await loadFriends();
      await loadRequests();
    }
  };

  const removeFriend = async (friendshipId: number) => {
    const res = await fetch(`${API}/friends/${friendshipId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setFriends(prev => prev.filter(f => f.friendship_id !== friendshipId));
      setRequests(prev => prev.filter(r => r.friendship_id !== friendshipId));
    }
  };

  return (
    <div className="friends-page">
      <SEO path="/friends" />
      <AppHeader onBack={smartBack} backLabel={t.common.back} />

      {message && <div className="friends-message">{message}</div>}

      <div className="friends-tabs">
        <button className={`tab-btn ${tab === 'friends' ? 'active' : ''}`} onClick={() => setTab('friends')}>
          {t.friends.tabFriends} {friends.length > 0 && <span className="tab-count">{friends.length}</span>}
        </button>
        <button className={`tab-btn ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
          {t.friends.tabRequests} {requests.length > 0 && <span className="tab-count tab-count--red">{requests.length}</span>}
        </button>
        <button className={`tab-btn ${tab === 'sent' ? 'active' : ''}`} onClick={() => setTab('sent')}>
          {t.friends.tabSent} {sent.length > 0 && <span className="tab-count">{sent.length}</span>}
        </button>
        <button className={`tab-btn ${tab === 'search' ? 'active' : ''}`} onClick={() => setTab('search')}>
          {t.friends.tabSearch}
        </button>
      </div>

      {tab === 'friends' && (
        <div className="friends-list">
          {friends.length === 0
            ? <p className="friends-empty">{t.friends.noFriendsHint}</p>
            : friends.map(f => (
              <div key={f.friendship_id} className="friend-item">
                <span className="friend-avatar" onClick={() => navigate(`/user/${f.user_id}`)}>{f.nickname[0].toUpperCase()}</span>
                <span className={`friend-online-dot ${onlineIds.has(f.user_id) ? 'online' : 'offline'}`} title={onlineIds.has(f.user_id) ? t.friends.online : t.friends.offline} />
                <span className="friend-name friend-name-link" onClick={() => navigate(`/user/${f.user_id}`)}>{f.nickname}</span>
                <button className="friend-history-btn" onClick={() => openMatchHistory(f.user_id)} disabled={loadingHistory}>
                  {t.friends.history}
                </button>
                <button className="friend-remove-btn" onClick={() => removeFriend(f.friendship_id)}>
                  {t.friends.removeBtn}
                </button>
              </div>
            ))
          }
        </div>
      )}

      {matchHistory && (
        <div className="match-history-overlay" onClick={() => setMatchHistory(null)}>
          <div className="match-history-modal" onClick={e => e.stopPropagation()}>
            <div className="match-history-header">
              <h3>{t.friends.historyTitle.replace('{nick}', matchHistory.friend.nickname)}</h3>
              <button className="match-history-close" onClick={() => setMatchHistory(null)}>✕</button>
            </div>
            <div className="match-history-summary">
              <span className="mh-wins">{matchHistory.wins} {t.friends.mhWins}</span>
              <span className="mh-sep">·</span>
              <span className="mh-losses">{matchHistory.losses} {t.friends.mhLosses}</span>
              {matchHistory.draws > 0 && <><span className="mh-sep">·</span><span>{matchHistory.draws} {t.friends.mhDraws}</span></>}
            </div>
            {matchHistory.matches.length === 0
              ? <p className="match-history-empty">{t.friends.noMatchHistory}</p>
              : <div className="match-history-list">
                  <div className="mh-row mh-row-head">
                    <span>{t.friends.tableDate}</span>
                    <span>{t.friends.tableScore}</span>
                    <span>{t.friends.tableTime}</span>
                    <span>{t.friends.tableMistakes}</span>
                    <span>{t.friends.tableResult}</span>
                  </div>
                  {matchHistory.matches.map(m => {
                    const won = m.my_place < m.friend_place;
                    const lost = m.my_place > m.friend_place;
                    return (
                      <div key={m.id} className={`mh-row ${won ? 'mh-win' : lost ? 'mh-loss' : ''}`}>
                        <span className="mh-date">{m.created_at ? new Date(m.created_at).toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit' }) : '—'}</span>
                        <span className="mh-score">{m.my_score} <span className="mh-vs">vs</span> {m.friend_score}</span>
                        <span className="mh-time">{formatTime(m.my_elapsed)} <span className="mh-vs">vs</span> {formatTime(m.friend_elapsed)}</span>
                        <span className="mh-mistakes">{m.my_mistakes} <span className="mh-vs">vs</span> {m.friend_mistakes}</span>
                        <span className={`mh-result ${won ? 'win' : lost ? 'loss' : 'draw'}`}>{won ? t.friends.mhResultWon : lost ? t.friends.mhResultLost : t.friends.mhResultDraw}</span>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div className="friends-list">
          {requests.length === 0
            ? <p className="friends-empty">{t.friends.noRequests}</p>
            : requests.map(r => (
              <div key={r.friendship_id} className="friend-item">
                <span className="friend-avatar" onClick={() => navigate(`/user/${r.user_id}`)}>{r.nickname[0].toUpperCase()}</span>
                <span className="friend-name friend-name-link" onClick={() => navigate(`/user/${r.user_id}`)}>{r.nickname}</span>
                <div className="friend-actions">
                  <button className="friend-accept-btn" onClick={() => acceptRequest(r.friendship_id)}>{t.friends.accept}</button>
                  <button className="friend-remove-btn" onClick={() => removeFriend(r.friendship_id)}>{t.friends.decline}</button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {tab === 'sent' && (
        <div className="friends-list">
          {sent.length === 0
            ? <p className="friends-empty">{t.friends.noSent}</p>
            : sent.map(s => (
              <div key={s.friendship_id} className="friend-item">
                <span className="friend-avatar" onClick={() => navigate(`/user/${s.user_id}`)}>{s.nickname[0].toUpperCase()}</span>
                <span className="friend-name friend-name-link" onClick={() => navigate(`/user/${s.user_id}`)}>{s.nickname}</span>
                <button className="friend-remove-btn" onClick={() => cancelRequest(s.friendship_id)}>
                  {t.friends.withdrawBtn}
                </button>
              </div>
            ))
          }
        </div>
      )}

      {tab === 'search' && (
        <div className="friends-search">
          <div className="friends-search-bar">
            <input
              type="text"
              placeholder={t.friends.searchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              className="friends-search-input"
            />
            <button className="friends-search-btn" onClick={search} disabled={searching}>
              {searching ? '...' : t.friends.searchBtn}
            </button>
          </div>
          <div className="friends-list">
            {searchResults.length === 0 && searchQuery.length >= 2 && !searching &&
              <p className="friends-empty">{t.friends.noResults}</p>
            }
            {searchResults.map(u => (
              <div key={u.user_id} className="friend-item">
                <span className="friend-avatar" onClick={() => navigate(`/user/${u.user_id}`)}>{u.nickname[0].toUpperCase()}</span>
                <span className="friend-name friend-name-link" onClick={() => navigate(`/user/${u.user_id}`)}>{u.nickname}</span>
                {!u.friendship_status
                  ? <button className="friend-add-btn" onClick={() => sendRequest(u.nickname)}>{t.friends.sendRequestBtn}</button>
                  : <span className="friend-status-chip">
                      {u.friendship_status === 'pending' ? t.friends.pending : t.friends.alreadyFriend}
                    </span>
                }
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
